// File Formats (admin → Platform Settings → File Formats): the extensions the
// Documents section accepts and what each opens in. The server is the
// authority (uploads are refused against the same list); these helpers only
// decide what a page offers.

export type DocumentViewer = 'pdf' | 'image' | 'download';
export interface FileFormat { extension: string; viewer: DocumentViewer }

export const DOCUMENT_VIEWERS: { id: DocumentViewer; label: string }[] = [
  { id: 'pdf', label: 'PDF Viewer' },
  { id: 'image', label: 'Image Viewer' },
  { id: 'download', label: 'Download only' },
];

// What the Image Viewer can show (the server checks the same list): what
// browsers draw themselves, plus TIFF, which the viewer decodes.
export const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg', 'avif', 'tif', 'tiff'];

export const DEFAULT_DOCUMENT_FORMATS: FileFormat[] = [{ extension: 'pdf', viewer: 'pdf' }];

export const normalizeExtension = (raw: string): string => raw.trim().toLowerCase().replace(/^\.+/, '');

const isViewer = (v: unknown): v is DocumentViewer => v === 'pdf' || v === 'image' || v === 'download';

/** What the server sent, or PDF in the PDF viewer while it is loading or could not be read. */
export function documentFormatsOf(raw: { extension?: unknown; viewer?: unknown }[] | null | undefined): FileFormat[] {
  const rows = (raw ?? [])
    .filter((r) => typeof r?.extension === 'string' && r.extension && isViewer(r.viewer))
    .map((r) => ({ extension: normalizeExtension(r.extension as string), viewer: r.viewer as DocumentViewer }));
  return rows.length ? rows : DEFAULT_DOCUMENT_FORMATS;
}

/**
 * Where a document opens. A format no longer on the list (taken off after
 * files were uploaded) keeps opening the way it always did: a PDF in the PDF
 * viewer, anything else as a download.
 */
export function documentOpensIn(extension: string | null | undefined, formats: FileFormat[]): DocumentViewer {
  const ext = normalizeExtension(extension ?? '');
  return formats.find((f) => f.extension === ext)?.viewer ?? (ext === 'pdf' ? 'pdf' : 'download');
}

/** The viewer page a document opens on, or null for a download-only format. */
export function documentViewerRoute(viewer: DocumentViewer): string | null {
  return viewer === 'pdf' ? 'viewer-pdf' : viewer === 'image' ? 'viewer-image' : null;
}

/** "PDF, DOCX" for a section's subtitle. */
export const documentFormatsLabel = (formats: FileFormat[]): string => formats.map((f) => f.extension.toUpperCase()).join(', ');

/** The first thing wrong with the rows being edited, or '' (the server checks the rest, such as other sections' formats). */
export function documentFormatsProblem(rows: { extension: string; viewer: string }[]): string {
  if (!rows.length) return 'Documents need at least one format';
  const seen = new Set<string>();
  for (const row of rows) {
    const ext = normalizeExtension(row.extension);
    if (!ext) return 'Every format needs an extension';
    if (!/^[a-z0-9]{1,16}$/.test(ext)) return `".${ext}" is not an extension: use 1–16 letters or digits`;
    if (seen.has(ext)) return `.${ext} is listed twice`;
    if (!isViewer(row.viewer)) return `Choose what .${ext} opens in`;
    if (row.viewer === 'image' && !IMAGE_EXTENSIONS.includes(ext)) return `The Image Viewer cannot show .${ext} files`;
    seen.add(ext);
  }
  return '';
}
